import type {
  AdmissionApplication,
  AdmissionCycle,
  AssessmentPathway,
  DocumentChecklistItem,
  IdNamePair,
  ApplicationStatus,
  CustomCriterion,
  InterviewRoundWithRubric,
  InterviewRubricItem,
  TestPaperConfig,
} from '../types/admissions';
import { fmtClassName } from './pdf/format';

/**
 * Assessment rules on the client — mirrors
 * backend/src/services/admissions/assessment.ts so the drawer, merit list
 * and decision screens show exactly what the server enforces.
 *
 *   - A stage is active only when its switch is on AND it is configured.
 *   - Test / interview are the assessment stages; custom criteria only feed merit.
 *   - The pass mark is passing/total marks, applied across all papers.
 */

export interface AssessmentPlan {
  admitCards: boolean;
  test: { papers: TestPaperConfig[]; maxMarks: number; passingMarks: number } | null;
  interview: { rounds: InterviewRoundWithRubric[] } | null;
  custom: { criteria: CustomCriterion[] } | null;
  hasAssessment: boolean;
}

/** Scored questions — the only ones that count toward a round's total. */
export const isScored = (c: Pick<InterviewRubricItem, 'kind'>): boolean => (c.kind ?? 'marks') === 'marks';

/** A round with scored questions is out of their total; otherwise it takes
 *  a single mark. Mirrors backend assessment.roundMax. */
export const roundMax = (r: Pick<InterviewRoundWithRubric, 'rubric' | 'maxMarks'>): number => {
  const scored = (r.rubric || []).filter(isScored);
  return scored.length ? scored.reduce((t, c) => t + (c.maxMarks || 0), 0) : r.maxMarks;
};

/** True when `v` is a whole number of `step`s (1.5 with half marks, not 1.3). */
export const onStep = (v: number, step = 1): boolean => Math.abs(v / step - Math.round(v / step)) < 1e-9;

export function assessmentPlan(pathway: Pick<AssessmentPathway, 'stages' | 'testConfig' | 'interviewConfig' | 'scoringConfig'>): AssessmentPlan {
  const stages = pathway.stages || ({} as AssessmentPathway['stages']);
  const papers = (pathway.testConfig?.papers || []).filter(p => p.maxMarks > 0);
  const rounds = (pathway.interviewConfig?.rounds || [])
    .map(r => ({ ...r, maxMarks: roundMax(r) }))
    .filter(r => r.maxMarks > 0);
  const criteria = (pathway.scoringConfig?.customCriteria || []).filter(c => c.enabled !== false && c.maxMarks > 0);
  const test = stages.testEnabled && papers.length
    ? { papers, maxMarks: pathway.testConfig.maxMarks, passingMarks: pathway.testConfig.passingMarks }
    : null;
  const interview = stages.interviewEnabled && rounds.length ? { rounds } : null;
  const custom = stages.customMarksEnabled && criteria.length ? { criteria } : null;
  return { admitCards: !!stages.admitCardEnabled, test, interview, custom, hasAssessment: !!(test || interview) };
}

const EMPTY_PATHWAY: AssessmentPathway = {
  key: '', name: '', classIds: [],
  stages: { admitCardEnabled: false, testEnabled: false, interviewEnabled: false, customMarksEnabled: false },
  testConfig: { maxMarks: 0, passingMarks: 0, papers: [] },
  interviewConfig: { rounds: [] },
  documentChecklist: [],
  scoringConfig: { customCriteria: [], meritFormula: { components: [], mode: 'weighted-sum', rounding: 2, scaleTo100: true } },
};

const idOf = (v: unknown): string => String((v as any)?._id ?? v ?? '');

/** The pathway a class follows: the one that lists it, else the cycle's
 *  first (default) pathway. Mirrors backend assessment.pathwayFor. */
export function pathwayFor<P extends Pick<AssessmentPathway, 'classIds'>>(
  cycle: { pathways?: P[] },
  classId: unknown,
): P {
  const pathways = cycle.pathways || [];
  const id = idOf(classId);
  return pathways.slice(1).find(p => (p.classIds || []).some(c => idOf(c) === id)) ?? pathways[0] ?? (EMPTY_PATHWAY as unknown as P);
}

/** An application's assessment plan — its class's pathway, read as a plan. */
export const planFor = (cycle: Pick<AdmissionCycle, 'pathways'>, app: Pick<AdmissionApplication, 'classId'>): AssessmentPlan =>
  assessmentPlan(pathwayFor(cycle, app.classId));

/** True when any pathway in the cycle scores applicants. */
export const cycleHasScoring = (cycle: Pick<AdmissionCycle, 'pathways'>): boolean =>
  (cycle.pathways || []).some(p => hasAnyScoring(assessmentPlan(p)));

/** What a pathway's assessment day is called on cards and schedules: the
 *  test when there is one, else its rounds ("Observation", "Interview"),
 *  "Assessment" when they mix. */
export function assessmentNoun(pathway: {
  stages?: { testEnabled?: boolean };
  interviewConfig?: { rounds?: Array<Pick<InterviewRoundWithRubric, 'kind'>> };
}): string {
  if (pathway.stages?.testEnabled) return 'Test';
  const kinds = new Set((pathway.interviewConfig?.rounds || []).map(roundNoun));
  return kinds.size === 1 ? Array.from(kinds)[0] : 'Assessment';
}

/** The classes a pathway covers, by name: its own list, or for the
 *  default pathway every offered class no other pathway lists. */
export function pathwayClassNames(cycle: Pick<AdmissionCycle, 'pathways' | 'allowedClassIds'>, pathway: Pick<AssessmentPathway, 'key'>): string[] {
  const classes = (cycle.allowedClassIds || []).filter((c): c is IdNamePair => typeof c === 'object');
  return classes.filter(c => pathwayFor(cycle, c._id).key === pathway.key).map(c => fmtClassName(c.name));
}

/** A document any pathway asks for, once, with the pathways that ask for
 *  it — for papers that span classes (the blank form, the export). `tag`
 *  says it in classes, as a parent reads it: "Required", "If applicable",
 *  "Class 7, 8 only", "Class JR-I to C-IX only", "All classes except Playgroup",
 *  "Required for Class 7, 8". */
export interface CycleDocument {
  item: DocumentChecklistItem;
  /** Names of the pathways that ask for it. */
  on: string[];
  tag: string;
}

export function documentsAcrossPathways(cycle: Pick<AdmissionCycle, 'pathways' | 'allowedClassIds'>): CycleDocument[] {
  const pathways = cycle.pathways || [];
  const byKey = new Map<string, { item: DocumentChecklistItem; on: AssessmentPathway[]; requiredOn: AssessmentPathway[] }>();
  for (const p of pathways) {
    for (const d of p.documentChecklist || []) {
      const entry = byKey.get(d.key) ?? { item: d, on: [], requiredOn: [] };
      entry.on.push(p);
      if (d.required) entry.requiredOn.push(p);
      byKey.set(d.key, entry);
    }
  }
  // Offered classes in the order the form lists them, without the "Class" prefix.
  const offered = (cycle.allowedClassIds || []).filter((c): c is IdNamePair => typeof c === 'object')
    .map(c => ({ n: fmtClassName(c.name).replace(/^Class\s+/i, ''), p: pathwayFor(cycle, c._id).key }));
  /** The classes a set of pathways covers, said shortest: "all classes except Playgroup",
   *  "Class JR-I to C-IX" for an unbroken run, else "Class 6, 7". */
  const classesOf = (ps: AssessmentPathway[]) => {
    const keys = new Set(ps.map(p => p.key));
    const idx = offered.flatMap((c, i) => (keys.has(c.p) ? [i] : []));
    const names = idx.map(i => offered[i].n);
    const left = offered.filter(c => !keys.has(c.p)).map(c => c.n);
    if (left.length && left.length < names.length && left.length <= 3) return { text: `all classes except ${left.join(', ')}`, all: true };
    const run = names.length >= 3 && idx[idx.length - 1] - idx[0] === idx.length - 1;
    return { text: `Class ${run ? `${names[0]} to ${names[names.length - 1]}` : names.join(', ')}`, all: false };
  };
  const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
  return Array.from(byKey.values()).map(({ item, on, requiredOn }) => {
    let tag: string;
    if (on.length < pathways.length) {
      const c = classesOf(on);
      tag = c.all ? cap(c.text) : `${c.text} only`;
    } else if (requiredOn.length === on.length) tag = 'Required';
    else if (!requiredOn.length) tag = 'If applicable';
    else tag = `Required for ${classesOf(requiredOn).text}`;
    return { item, on: on.map(p => p.name), tag };
  });
}

/** "Round" words for a round: observations read as such on every screen. */
export const roundNoun = (r: Pick<InterviewRoundWithRubric, 'kind'>): string => (r.kind === 'observation' ? 'Observation' : 'Interview');

/** What a set of rounds is called together: "Interview", "Observation",
 *  or "Interview & observation" — column headers and group labels. */
export function roundsLabel(rounds: Array<Pick<InterviewRoundWithRubric, 'kind'>>): string {
  const kinds = Array.from(new Set(rounds.map(roundNoun))).sort();
  if (!kinds.length) return 'Interview';
  return [kinds[0], ...kinds.slice(1).map(k => k.toLowerCase())].join(' & ');
}

/** True when the drawer's Assessment tab has anything to show. */
export const hasAnyScoring = (plan: AssessmentPlan) => !!(plan.test || plan.interview || plan.custom);

export interface TestOutcome { obtained: number; max: number; passed: boolean }

/** Marks across all papers vs the pass mark; null until every paper is marked. */
export function testOutcome(app: Pick<AdmissionApplication, 'testScores'>, plan: AssessmentPlan): TestOutcome | null {
  if (!plan.test) return null;
  const byKey = new Map((app.testScores || []).map(s => [s.paperKey, s]));
  let obtained = 0;
  let max = 0;
  for (const p of plan.test.papers) {
    const s = byKey.get(p.key);
    if (!s) return null;
    obtained += s.absent ? 0 : s.obtained;
    max += p.maxMarks;
  }
  const share = plan.test.maxMarks > 0 ? plan.test.passingMarks / plan.test.maxMarks : 0;
  return { obtained, max, passed: max > 0 && obtained / max >= share - 1e-9 };
}

/** Papers and rounds still without marks. */
export function assessmentGaps(app: Pick<AdmissionApplication, 'testScores' | 'interviewRounds'>, plan: AssessmentPlan): string[] {
  const scored = new Set((app.testScores || []).map(s => s.paperKey));
  const interviewed = new Set((app.interviewRounds || []).map(r => r.roundKey));
  return [
    ...(plan.test?.papers || []).filter(p => !scored.has(p.key)).map(p => `${p.subject} paper`),
    ...(plan.interview?.rounds || []).filter(r => !interviewed.has(r.key)).map(r => r.name),
  ];
}

/** Statuses in which marks can be entered or corrected. */
export const SCORE_EDITABLE: ReadonlySet<ApplicationStatus> = new Set<ApplicationStatus>([
  'UnderReview', 'DocsRequired', 'AssessmentScheduled', 'Absent', 'Assessed',
]);
/** Test / interview marks need the assessment to be scheduled. */
export const ASSESSMENT_MARKING: ReadonlySet<ApplicationStatus> = new Set<ApplicationStatus>(['AssessmentScheduled', 'Assessed']);

/** Why marks can't be entered for this applicant right now (null when they
 *  can) — for each kind of mark. Mirrors the server's rules; the drawer and
 *  the marks spreadsheet both use it. */
export function markingBlockers(
  app: Pick<AdmissionApplication, 'status' | 'admitCard'>,
  plan: AssessmentPlan,
): { custom: string | null; interview: string | null; test: string | null } {
  const custom = !SCORE_EDITABLE.has(app.status)
    ? `Marks are locked once the application is ${app.status === 'Offered' ? 'offered a seat' : 'decided'}.`
    : null;
  const interview = custom
    ?? (app.status === 'Absent' ? 'The applicant is marked absent — reschedule the assessment to enter marks.'
      : !ASSESSMENT_MARKING.has(app.status) ? 'Schedule the assessment to enter test and interview marks.'
        : null);
  const test = interview ?? (plan.admitCards && !app.admitCard?.rollNumber ? 'Issue the admit card before entering test marks.' : null);
  return { custom, interview, test };
}

/** Error payload when an offer breaks merit order or the pass mark. */
/** The eligibility failures still standing — none once the school has
 *  waived the rules. Mirrors the backend's `unwaivedFailures`. */
export const eligibilityFailures = (app: Pick<AdmissionApplication, 'eligibilityFlags' | 'eligibilityWaiver'>): string[] =>
  app.eligibilityWaiver ? [] : (app.eligibilityFlags || []).filter(f => f.severity === 'fail').map(f => f.message);

export interface DecisionRuleBlock {
  code: 'DECISION_OVERRIDE_REQUIRED';
  reasons: string[];
  canRequestOverride: boolean;
}

export const decisionRuleFromError = (err: any): DecisionRuleBlock | null =>
  err?.data?.code === 'DECISION_OVERRIDE_REQUIRED' ? (err.data as DecisionRuleBlock) : null;

/** The open approval request on an application, with its index (the API addresses it by index). */
export const pendingOverride = (app: Pick<AdmissionApplication, 'pendingDecisionOverrides'>) => {
  const index = (app.pendingDecisionOverrides || []).findIndex(o => o.status === 'pending');
  return index >= 0 ? { index, request: app.pendingDecisionOverrides[index] } : null;
};
